import React from "react";
import { Box, Tooltip } from "@mui/material";
import { MenuBook, ReceiptLong } from "@mui/icons-material";

const ACCENT = "#674BF5";

// Track/thumb geometry kept in constants so the thumb travel stays exact if sizes change.
const PADDING = 3;
const SLOT = 30; // width of each icon half, and the thumb diameter
const TRACK_HEIGHT = 38;

/**
 * A two-ended segmented switch for flipping a statement table between its normal
 * view and Ledger View — styled like a light/dark theme switch: a pill track with
 * an icon at each end and a thumb that slides over the active one.
 *
 * Left icon (receipt)  = normal statement view.
 * Right icon (ledger book) = ledger view with running balance.
 *
 * @param {Object} props
 * @param {boolean} [props.isLedgerMode] - Whether ledger view is currently active.
 * @param {function} [props.onToggle] - Called when the switch is flipped.
 * @param {boolean} [props.disabled] - Whether the switch is inert.
 */
export const LedgerModeToggle = ({ isLedgerMode = false, onToggle = () => {}, disabled = false }) => {
  const handleToggle = () => {
    if (!disabled) onToggle();
  };

  const handleKeyDown = (event) => {
    // keep the switch operable without a mouse
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <Tooltip title={isLedgerMode ? "Switch to Statement View" : "Switch to Ledger View"}>
      <Box
        role="switch"
        aria-checked={isLedgerMode}
        aria-label="Ledger view"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          height: TRACK_HEIGHT,
          padding: `${PADDING}px`,
          borderRadius: TRACK_HEIGHT / 2,
          border: `1px solid ${ACCENT}`,
          backgroundColor: "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          userSelect: "none",
          "&:focus-visible": {
            outline: `2px solid ${ACCENT}`,
            outlineOffset: 2,
          },
        }}
      >
        {/* sliding thumb sitting under the icons */}
        <Box
          sx={{
            position: "absolute",
            top: PADDING,
            left: PADDING,
            width: SLOT,
            height: SLOT,
            borderRadius: "50%",
            backgroundColor: ACCENT,
            transition: "transform 200ms ease",
            transform: isLedgerMode ? `translateX(${SLOT}px)` : "translateX(0)",
          }}
        />

        {/* statement side */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: SLOT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isLedgerMode ? ACCENT : "#fff",
            transition: "color 200ms ease",
          }}
        >
          <ReceiptLong sx={{ fontSize: "1.1rem" }} />
        </Box>

        {/* ledger side */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: SLOT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isLedgerMode ? "#fff" : ACCENT,
            transition: "color 200ms ease",
          }}
        >
          <MenuBook sx={{ fontSize: "1.1rem" }} />
        </Box>
      </Box>
    </Tooltip>
  );
};
